import { injectable } from 'tsyringe';
import { NoRecommendationReasons } from '../no-recommendations-reasons.enums';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import {
  conditionType,
  ConditionType,
} from './decrators/condition-type.decorator';
import { LoggerService } from '../../../../shared/services/logger.service';
import { UserActionService } from '../../user-action.service';
import { IUserActionServiceDelegate } from '../../../../shared/interfaces/user-action-service.interface';
import { PromotionDisplayDetails } from '../../../../shared/models/promotion-display-details.model';
import { Game } from '../../../models/game.model';
import { PromotionType } from '../../datastore-service/models/raw-promotion.model';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Promotion)
export class ConditionPromotionOfferDismissed
  implements ICondition, IUserActionServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionPromotionOfferDismissed',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentLocalStorage: PersistentStorageService,
    private readonly userActionService: UserActionService,
  ) {
    this.userActionService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  public shouldSkip(context: ConditionContext) {
    const { promotion } = context;

    if (!promotion?.type || promotion.type !== PromotionType.Offer) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { game, promotion } = context;

    const dismissedTimestamp = this.getApplicationDismissedTimestamp(game);

    if (!dismissedTimestamp || dismissedTimestamp === 0) {
      return [true];
    }

    this.logger.info(
      `${promotion?.name} was previously dismissed by the user.`,
    );
    return [false, NoRecommendationReasons.DismissedByUser];
  }

  // ---------------------------------------------------------------------------
  private getApplicationDismissedTimestamp(game: Game): number {
    const displayedPromotion =
      this.persistentLocalStorage.getDisplayedPromotionOfferInfo(game);

    return displayedPromotion?.dismissedTimestamp ?? 0;
  }

  // ---------------------------------------------------------------------------
  // IUserActionServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onUserActionDismissed(activePromotion: PromotionDisplayDetails) {
    const { promotionId, gameId, promotionType } = activePromotion;

    if (promotionType !== PromotionType.Offer) {
      return;
    }

    this.logger.info(
      `The promotion (${gameId}, ${promotionId}) was Dismissed.`,
    );

    this.persistentLocalStorage.setPromotionDismissed(gameId);
  }
}
